import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Input } from '../../../../components-v2/Input';
import { THEME } from '../../../../components-v2/Theme/theme';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/Select';
import { Separator } from '../../../../components/Separator';
import { Toggle } from '../../../../components/Toggle';
import { styleSheet } from '../styles';
import { DonationServiceFeeCalculationMethod, DonationSurveyFormData } from '../utils';
import { DonationFrequencySettingsSection } from './DonationFrequencySettingsSection';

/**
 * Human-readable labels for service fee calculation methods.
 * Maps internal enum values to display strings for the UI.
 */
const serviceFeeCalculationMethodNames: Record<DonationServiceFeeCalculationMethod, string> = {
	[DonationServiceFeeCalculationMethod.STRIPE]: 'Stripe',
	[DonationServiceFeeCalculationMethod.FIXED]: 'Fixed',
	[DonationServiceFeeCalculationMethod.PERCENTAGE]: 'Percentage',
};

/**
 * Dropdown options for service fee calculation method selection.
 * Converts enum values to label-value pairs for the Select component.
 */
const serviceFeeCalculationOptions = Object.values(DonationServiceFeeCalculationMethod).map(method => ({
	label: serviceFeeCalculationMethodNames[method],
	value: method,
}));

/**
 * DonationOptions Component
 *
 * Main container for all donation settings including frequency options and service fees.
 * Orchestrates three independent donation frequency sections (one-time, monthly, annual)
 * and provides controls for processing fee coverage.
 *
 * Structure:
 * - One-Time Donations Section (with enable toggle)
 * - Monthly Donations Section (with enable toggle) - only shown if customerPortalUrl configured
 * - Annual Donations Section (with enable toggle) - only shown if customerPortalUrl configured
 * - Service Fee Settings (toggle + calculation method selection)
 *
 * Features:
 * - Each frequency can be independently enabled/disabled
 * - Service fees can use Stripe's default, fixed amount, or percentage
 * - Currency input formatting for fixed fees (displays with $ symbol)
 * - Percentage input formatting (displays with % symbol)
 * - All controls disabled when survey is archived
 * - Recurring donation options (monthly/annual) require customerPortalUrl to be configured
 *
 * @param survey - The donation survey being edited
 * @param customerPortalUrl - Stripe customer portal URL from admin settings (required for recurring donations)
 */
export const DonationOptions: React.FC<{ survey: Api.IDonationSurvey; customerPortalUrl?: string }> = ({
	survey,
	customerPortalUrl,
}) => {
	const isArchived = !!survey?.archivedDate;
	const hasCustomerPortal = Boolean(customerPortalUrl);

	const {
		control,
		watch,
		setValue,
		// formState: { errors },
	} = useFormContext<DonationSurveyFormData>();
	const formValues = watch();

	// "Last one standing" rule: Ensure at least one frequency is always enabled
	// If all frequencies are disabled, automatically re-enable one-time donations
	// When customer portal is not configured, recurring options don't count as "available"
	React.useEffect(() => {
		const oneTimeEnabled = formValues.oneTimeSettings?.enabled;
		const monthlyEnabled = hasCustomerPortal && formValues.monthlySettings?.enabled;
		const annualEnabled = hasCustomerPortal && formValues.annualSettings?.enabled;

		// If all available frequencies are disabled, force-enable one-time
		if (!oneTimeEnabled && !monthlyEnabled && !annualEnabled) {
			setValue('oneTimeSettings.enabled', true);
		}

		// If customer portal is not configured, disable recurring options
		if (!hasCustomerPortal) {
			if (formValues.monthlySettings?.enabled) {
				setValue('monthlySettings.enabled', false);
			}
			if (formValues.annualSettings?.enabled) {
				setValue('annualSettings.enabled', false);
			}
		}
	}, [
		formValues.oneTimeSettings?.enabled,
		formValues.monthlySettings?.enabled,
		formValues.annualSettings?.enabled,
		hasCustomerPortal,
		setValue,
	]);

	return (
		<>
			<h2 className={css(styleSheet.sectionTitle, styleSheet.donationOptionsTitle)}>Donation Options</h2>
			{/* One-Time Donations Section */}
			<DonationFrequencySettingsSection frequencyType='oneTime' displayName='One-Time' isArchived={isArchived} />
			<Separator styles={[styleSheet.separator]} />

			{/* Monthly Donations Section - Only shown if customer portal is configured */}
			{hasCustomerPortal ? (
				<>
					<DonationFrequencySettingsSection frequencyType='monthly' displayName='Monthly' isArchived={isArchived} />
					<Separator styles={[styleSheet.separator]} />
				</>
			) : (
				<>
					<section className={css(styleSheet.section)}>
						<div className={css(styleSheet.formField)}>
							<div className={css(styleSheet.formFieldLabelContainer)}>
								<label className={css(styleSheet.formFieldLabel)}>Monthly Donations (Unavailable)</label>
								<span className={css(styleSheet.formFieldDescription)}>
									To enable monthly donations, please configure the Payment Processing URL in Admin Settings.
								</span>
							</div>
						</div>
					</section>
					<Separator styles={[styleSheet.separator]} />
				</>
			)}

			{/* Annual Donations Section - Only shown if customer portal is configured */}
			{hasCustomerPortal ? (
				<>
					<DonationFrequencySettingsSection frequencyType='annual' displayName='Annual' isArchived={isArchived} />
					<Separator styles={[styleSheet.separator]} />
				</>
			) : (
				<>
					<section className={css(styleSheet.section)}>
						<div className={css(styleSheet.formField)}>
							<div className={css(styleSheet.formFieldLabelContainer)}>
								<label className={css(styleSheet.formFieldLabel)}>Annual Donations (Unavailable)</label>
								<span className={css(styleSheet.formFieldDescription)}>
									To enable annual donations, please configure the Payment Processing URL in Admin Settings.
								</span>
							</div>
						</div>
					</section>
					<Separator styles={[styleSheet.separator]} />
				</>
			)}

			{/* Service Fee Settings */}
			<section className={css(styleSheet.section)}>
				<div className={css(styleSheet.formField)}>
					<div className={css(styleSheet.formFieldLabelContainer)}>
						<div className={css(styleSheet.formFieldLabelWithToggleContainer)}>
							<label htmlFor={'allow-customer-to-cover-service-fees-toggle'} className={css(styleSheet.formFieldLabel)}>
								Ask Donors to cover processing fees
							</label>

							<Controller
								name='allowCustomerToCoverServiceFees'
								control={control}
								render={({ field: { ref, ...fieldProps } }) => (
									<Toggle
										id='allow-customer-to-cover-service-fees-toggle'
										isOn={fieldProps.value}
										onToggleCheckChanged={fieldProps.onChange}
										uncheckedColor={THEME.colors.background.dark}
									/>
								)}
							/>
						</div>

						<span className={css(styleSheet.formFieldDescription)}>
							Ask donors to cover the processing fee with a specific amount or percentage
						</span>
					</div>

					<div className={css(styleSheet.serviceFeeCalculationMethodContainer)}>
						<Controller
							name='serviceFeeCalculationMethod'
							control={control}
							render={({ field: { ref, ...fieldProps } }) => {
								return (
									<Select
										disabled={isArchived || !formValues.allowCustomerToCoverServiceFees}
										value={fieldProps.value}
										onValueChange={fieldProps.onChange}
									>
										<SelectTrigger>
											<SelectValue placeholder='Please select option' />
										</SelectTrigger>
										<SelectContent>
											{serviceFeeCalculationOptions.map(calculationOption => (
												<SelectItem key={calculationOption.value} value={calculationOption.value}>
													{calculationOption.label}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								);
							}}
						/>

						{formValues.serviceFeeCalculationMethod === DonationServiceFeeCalculationMethod.FIXED && (
							<Controller
								name='serviceFeeFixedAmount'
								control={control}
								render={({ field: { ref, ...fieldProps } }) => (
									<Input
										type='text'
										value={fieldProps.value ? `$${fieldProps.value}` : ''}
										onChange={e => {
											const inputValue = e.target.value;
											// Remove $ and any non-numeric characters except decimal point
											const numericValue = inputValue.replace(/[^0-9.]/g, '');
											const parsedValue = parseFloat(numericValue);

											if (numericValue === '' || numericValue === '.') {
												fieldProps.onChange(0);
											} else if (!isNaN(parsedValue)) {
												fieldProps.onChange(parsedValue);
											}
										}}
										placeholder='$0'
									/>
								)}
							/>
						)}

						{formValues.serviceFeeCalculationMethod === DonationServiceFeeCalculationMethod.PERCENTAGE && (
							<Controller
								name='serviceFeePercentage'
								control={control}
								render={({ field: { ref, ...fieldProps } }) => (
									<Input
										type='text'
										value={fieldProps.value ? `${fieldProps.value}%` : ''}
										onChange={e => {
											const inputValue = e.target.value;
											// Remove % and any non-numeric characters except decimal point
											const numericValue = inputValue.replace(/[^0-9.]/g, '');
											const parsedValue = parseFloat(numericValue);

											if (numericValue === '' || numericValue === '.') {
												fieldProps.onChange(0);
											} else if (!isNaN(parsedValue)) {
												fieldProps.onChange(parsedValue);
											}
										}}
										placeholder='0%'
									/>
								)}
							/>
						)}
					</div>
				</div>
			</section>
		</>
	);
};
